import { IconButton, NumberField, PropRow, SelectField, Toggle } from '../ui/controls.js';
import {
  INTERRUPTIONS,
  allMachines,
  allStates,
  defaultCondition,
  opsFor,
  type CondOp,
  type ControllerDoc,
  type Interruption,
  type Layer,
  type Transition,
} from './controller-doc.js';

/** Timing and conditions of one transition. `mutate` edits it in place inside an `edit` call. */
export function TransitionInspector({
  doc,
  layer,
  from,
  t,
  isAny,
  mutate,
  onDelete,
}: {
  doc: ControllerDoc;
  layer: Layer;
  from: string;
  t: Transition;
  isAny: boolean;
  mutate: (fn: (t: Transition) => void) => void;
  onDelete: () => void;
}) {
  const targets = [...allStates(layer).map((s) => s.full), ...allMachines(layer)];
  const conditions = t.conditions ?? [];
  const params = doc.parameters;

  return (
    <div className="anim-inspector-body">
      <div className="anim-inspector-title">
        <span className="mono">{from === '@any' ? 'Any State' : from}</span>
        <span className="muted"> → </span>
        <span className="mono">{t.to}</span>
        <IconButton icon="trash" title="Delete transition" onClick={onDelete} />
      </div>
      <PropRow label="Destination" hint="State, or a sub-state machine (enters its default state)">
        <SelectField value={t.to} options={targets} onChange={(to) => mutate((x) => void (x.to = to))} />
      </PropRow>
      <PropRow label="Has exit time" hint="Only fire once the source reaches Exit time">
        <Toggle checked={t.hasExitTime === true} onChange={(v) => mutate((x) => (v ? (x.hasExitTime = true) : delete x.hasExitTime))} />
      </PropRow>
      {t.hasExitTime && (
        <PropRow label="Exit time" hint="Normalized source time; below 1 on a looping state it is checked every loop">
          <NumberField value={t.exitTime ?? 1} step={0.05} min={0} onChange={(v) => mutate((x) => void (x.exitTime = v))} />
        </PropRow>
      )}
      <PropRow label="Fixed duration" hint="Duration in seconds (on) or as a fraction of the source state's length (off)">
        <Toggle checked={t.fixedDuration !== false} onChange={(v) => mutate((x) => (v ? delete x.fixedDuration : (x.fixedDuration = false)))} />
      </PropRow>
      <PropRow label={t.fixedDuration === false ? 'Duration (fraction)' : 'Duration (s)'}>
        <NumberField value={t.duration ?? 0.25} step={0.05} min={0} onChange={(v) => mutate((x) => void (x.duration = v))} />
      </PropRow>
      <PropRow label="Offset" hint="Normalized start time in the destination state">
        <NumberField value={t.offset ?? 0} step={0.05} min={0} max={1} onChange={(v) => mutate((x) => (v ? (x.offset = v) : delete x.offset))} />
      </PropRow>
      <PropRow label="Interruption" hint="Which transitions may interrupt this one while it blends">
        <SelectField
          value={t.interruptionSource ?? 'none'}
          options={INTERRUPTIONS}
          onChange={(v) => mutate((x) => (v === 'none' ? delete x.interruptionSource : (x.interruptionSource = v as Interruption)))}
        />
      </PropRow>
      {isAny && (
        <PropRow label="Can transition to self" hint="May restart the state that is already playing">
          <Toggle checked={t.canTransitionToSelf === true} onChange={(v) => mutate((x) => (v ? (x.canTransitionToSelf = true) : delete x.canTransitionToSelf))} />
        </PropRow>
      )}

      <div className="anim-section-head">
        <span>Conditions</span>
        <button
          type="button"
          className="anim-chip"
          disabled={params.length === 0}
          title={params.length ? 'Add a condition' : 'Add a parameter first'}
          onClick={() => mutate((x) => void (x.conditions = [...(x.conditions ?? []), defaultCondition(params[0])]))}
        >
          + condition
        </button>
      </div>
      {conditions.length === 0 && (
        <p className="muted anim-hint">{t.hasExitTime ? 'Fires at the exit time.' : 'No conditions and no exit time: fires immediately.'}</p>
      )}
      {conditions.map((c, i) => {
        const p = params.find((q) => q.name === c.param);
        const ops = opsFor(p?.type);
        return (
          <div key={i} className="anim-cond">
            <SelectField
              value={c.param}
              options={params.map((q) => q.name)}
              onChange={(name) =>
                mutate((x) => {
                  const q = params.find((pp) => pp.name === name);
                  x.conditions![i] = defaultCondition(q);
                })
              }
            />
            {ops.length > 0 && (
              <SelectField value={c.op ?? ops[0]} options={ops} onChange={(op) => mutate((x) => void (x.conditions![i]!.op = op as CondOp))} />
            )}
            {(p?.type === 'float' || p?.type === 'int') && (
              <NumberField
                value={c.value ?? 0}
                integer={p.type === 'int'}
                step={p.type === 'int' ? 1 : 0.1}
                onChange={(v) => mutate((x) => void (x.conditions![i]!.value = v))}
              />
            )}
            <IconButton
              icon="x"
              title="Remove condition"
              onClick={() =>
                mutate((x) => {
                  x.conditions!.splice(i, 1);
                  if (x.conditions!.length === 0) delete x.conditions;
                })
              }
            />
          </div>
        );
      })}
    </div>
  );
}
